<template>
  <Skeleton
    :shape="shape"
    :size="size"
    :width="width"
    :height="height"
    :borderRadius="borderRadius"
    :animation="animation"
    :class="[
      className,
      {
        'p-skeleton-responsive': responsive,
        'p-skeleton-rtl': dir === 'rtl'
      }
    ]"
    :style="style"
    :role="'progressbar'"
    :aria-label="ariaLabel"
    :aria-busy="true"
    :aria-valuemin="0"
    :aria-valuemax="100"
    :aria-valuetext="ariaLoadingText"
  >
    <slot></slot>
  </Skeleton>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Skeleton from 'primevue/skeleton'
import type { MOPSkeletonProps } from './types'

export default defineComponent({
  name: 'MOPSkeleton',
  components: {
    Skeleton
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
withDefaults(defineProps<MOPSkeletonProps>(), {
  shape: 'rectangle',
  size: undefined,
  width: '100%',
  height: '1rem',
  borderRadius: undefined,
  animation: 'wave',
  className: undefined,
  style: undefined,
  responsive: true,
  dir: 'ltr',
  ariaLabel: 'Loading content',
  ariaLoadingText: 'Please wait while content is loading'
})
</script>

<style lang="scss">
.p-skeleton {
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    background: linear-gradient(
      90deg,
      transparent,
      var(--skeleton-animation-color, rgba(255, 255, 255, 0.4)),
      transparent
    );
  }

  &.p-skeleton-animation-wave::after {
    animation: p-skeleton-wave 1.5s infinite;
  }

  &.p-skeleton-animation-none::after {
    animation: none;
  }

  &-circle {
    border-radius: 50%;
  }

  &-responsive {
    @media screen and (max-width: 576px) {
      &[class*="p-skeleton-w-"],
      &[class*="p-skeleton-h-"] {
        width: 100% !important;
        height: auto !important;
      }
    }
  }

  &-rtl {
    direction: rtl;
    &::after {
      transform: translateX(100%);
      animation-direction: reverse;
    }
  }
}

@keyframes p-skeleton-wave {
  100% {
    transform: translateX(100%);
  }
}

// Theme integration
:root {
  --skeleton-bg: var(--surface-200, #e9ecef);
  --skeleton-animation-color: rgba(255, 255, 255, 0.4);
}

// High contrast mode support
@media screen and (-ms-high-contrast: active) {
  .p-skeleton {
    border: 1px solid currentColor;
    &::after {
      background: none;
    }
  }
}
</style>
